import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import storeManager from '../../store/StoreManager';
import uiActions from '../../actions/UIActions';
import { t } from '../../services/i18nService';
import PciResourceRuntimeHost from './PciResourceRuntimeHost';
import styles from './pciResourceOverlay.scss';

const OVERLAY_WIDTH = 470;
const OVERLAY_HEIGHT = 700;
const MIN_VISIBLE = 150;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 200;
// Keeps every edge (including resize handles) reachable within the viewport.
const EDGE_MARGIN = 20;
// The app chrome the overlay must never be hidden behind, keyed by their DOM ids.
const HEADER_ELEMENT_ID = 'header';
const FOOTER_ELEMENT_ID = 'footer-wrapper';

class PciResourceOverlayModal extends React.PureComponent {
    static propTypes = {
        contentItemId: PropTypes.number.isRequired,
        loading: PropTypes.bool.isRequired,
        error: PropTypes.string,
        runtime: PropTypes.object,
        minimized: PropTypes.bool.isRequired,
        title: PropTypes.string,
    };

    constructor(props) {
        super(props);
        const initialX = Math.max(window.innerWidth - OVERLAY_WIDTH - EDGE_MARGIN, EDGE_MARGIN);
        this.state = {
            ...this.clampToViewport(OVERLAY_WIDTH, OVERLAY_HEIGHT, initialX, 60),
            resizing: false,
        };
        this.resizeMouseMove = null;
        this.resizeMouseUp = null;
        // Mirrors state.resizing but is set/read synchronously (unlike React state, which is
        // batched): handleWindowResize can be invoked by a ResizeObserver callback firing in the
        // same tick as startResize's setState({ resizing: true }), before that update has been
        // flushed - reading this.state.resizing there can momentarily see the stale `false`.
        this.isResizing = false;
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleWindowResize);
        this.observeChromeElements();
    }

    componentWillUnmount() {
        this.detachResizeListeners();
        window.removeEventListener('resize', this.handleWindowResize);
        if (this.chromeResizeObserver) {
            this.chromeResizeObserver.disconnect();
            this.chromeResizeObserver = null;
        }
    }

    // Watches the header and footer chrome for size changes that aren't window resizes
    // (e.g. a rubric bar appearing in the footer), so the overlay re-clamps around them too.
    observeChromeElements = () => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        this.chromeResizeObserver = new ResizeObserver(this.handleWindowResize);
        const headerEl = document.getElementById(HEADER_ELEMENT_ID);
        const footerEl = document.getElementById(FOOTER_ELEMENT_ID);
        if (headerEl) {
            this.chromeResizeObserver.observe(headerEl);
        }
        if (footerEl) {
            this.chromeResizeObserver.observe(footerEl);
        }
    };

    // The vertical band the overlay must stay within: below the header, above the footer,
    // each with a visible margin - measured live since both can change height
    // (theme, short-viewport layouts, rubric visibility) independently of window size.
    getSafeVerticalBounds = () => {
        const headerEl = document.getElementById(HEADER_ELEMENT_ID);
        const footerEl = document.getElementById(FOOTER_ELEMENT_ID);
        const top = headerEl ? headerEl.getBoundingClientRect().bottom + EDGE_MARGIN : EDGE_MARGIN;
        const bottomRaw = footerEl
            ? footerEl.getBoundingClientRect().top - EDGE_MARGIN
            : window.innerHeight - EDGE_MARGIN;
        return { top, bottom: Math.max(bottomRaw, top + MIN_HEIGHT) };
    };

    // Ensures the float's full box - including resize handles on its far edges -
    // always stays within the current viewport and clear of the header/footer,
    // both on open and on window/chrome resize.
    clampToViewport = (width, height, x, y) => {
        const { top: safeTop, bottom: safeBottom } = this.getSafeVerticalBounds();
        const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - (EDGE_MARGIN * 2));
        const maxHeight = Math.max(MIN_HEIGHT, safeBottom - safeTop);
        const clampedWidth = Math.min(width, maxWidth);
        const clampedHeight = Math.min(height, maxHeight);
        const maxX = Math.max(EDGE_MARGIN, window.innerWidth - clampedWidth - EDGE_MARGIN);
        const clampedX = Math.min(Math.max(x, EDGE_MARGIN), maxX);
        const clampedY = Math.min(Math.max(y, safeTop), safeBottom - clampedHeight);
        return {
            size: { width: clampedWidth, height: clampedHeight },
            position: { x: clampedX, y: clampedY },
        };
    };

    handleWindowResize = () => {
        // Skip while the user is actively dragging a resize handle: this can fire mid-gesture
        // (the ResizeObserver watches the header/footer, which can shift as the layout responds),
        // and re-clamping here would clobber the size/position the live resize is computing.
        // Checked via the synchronous instance flag, not state.resizing - a ResizeObserver
        // callback can fire in the same tick as startResize's setState, before React has
        // flushed it, which would otherwise let this slip through and stomp the live resize.
        if (this.isResizing) {
            return;
        }
        this.setState(prevState => this.clampToViewport(
            prevState.size.width,
            prevState.size.height,
            prevState.position.x,
            prevState.position.y
        ));
    };

    detachResizeListeners = () => {
        if (this.resizeMouseMove) {
            document.removeEventListener('mousemove', this.resizeMouseMove);
            this.resizeMouseMove = null;
        }
        if (this.resizeMouseUp) {
            document.removeEventListener('mouseup', this.resizeMouseUp);
            this.resizeMouseUp = null;
        }
    };

    handleMinimize = () => {
        const { contentItemId, minimized } = this.props;
        storeManager.dispatch(uiActions.uiSetPciResourceMinimized(contentItemId, !minimized));
    };

    handleHeaderDoubleClick = () => {
        const { contentItemId, minimized } = this.props;
        storeManager.dispatch(uiActions.uiSetPciResourceMinimized(contentItemId, !minimized));
    };

    handleClose = () => {
        const { contentItemId } = this.props;
        storeManager.dispatch(uiActions.uiClosePciResourceOverlay(contentItemId));
    };

    // Surfaces a PCI module load failure (reported by the sandboxed iframe itself, after the
    // initial runtime fetch already succeeded) into Redux, so overlayOpen flips to false and
    // clicking the footer button again retries instead of silently no-op'ing.
    handleRuntimeLoadError = () => {
        const { contentItemId } = this.props;
        storeManager.dispatch(uiActions.uiSetPciResourceError(contentItemId, 'pci-resource-runtime-error'));
    };

    handleDrag = (e, data) => {
        this.setState({ position: { x: data.x, y: data.y } });
    };

    getDraggableBounds() {
        const { size } = this.state;
        return {
            left: -(size.width - MIN_VISIBLE),
            right: window.innerWidth - MIN_VISIBLE,
            top: 0,
            bottom: window.innerHeight - MIN_VISIBLE,
        };
    }

    startResize = (e, directions) => {
        e.preventDefault();
        e.stopPropagation();
        this.detachResizeListeners();
        const { size, position } = this.state;
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = size.width;
        const startHeight = size.height;
        const startPosX = position.x;
        const startPosY = position.y;

        this.isResizing = true;
        this.setState({ resizing: true });

        const onMouseMove = (moveEvent) => {
            const newSize = { width: startWidth, height: startHeight };
            const newPosition = { ...position };

            if (directions.includes('e')) {
                const maxWidthE = window.innerWidth - position.x - EDGE_MARGIN;
                newSize.width = Math.min(
                    Math.max(MIN_WIDTH, startWidth + (moveEvent.clientX - startX)),
                    maxWidthE
                );
            }
            if (directions.includes('w')) {
                // Left edge: width grows leftward, so x must shift left by the same amount.
                const rawWidth = startWidth - (moveEvent.clientX - startX);
                const maxWidthW = (startPosX + startWidth) - EDGE_MARGIN;
                newSize.width = Math.min(Math.max(MIN_WIDTH, rawWidth), maxWidthW);
                newPosition.x = startPosX - (newSize.width - startWidth);
            }
            if (directions.includes('s')) {
                const maxHeightS = window.innerHeight - position.y - EDGE_MARGIN;
                newSize.height = Math.min(
                    Math.max(MIN_HEIGHT, startHeight + (moveEvent.clientY - startY)),
                    maxHeightS
                );
            }
            if (directions.includes('n')) {
                // Top edge: height grows upward, so y must shift up by the same amount.
                // Clamped against the header (not just EDGE_MARGIN) so the overlay's own header/
                // drag handle can never end up under the site header, unreachable.
                const { top: safeTop } = this.getSafeVerticalBounds();
                const rawHeight = startHeight - (moveEvent.clientY - startY);
                const maxHeightN = (startPosY + startHeight) - safeTop;
                newSize.height = Math.min(Math.max(MIN_HEIGHT, rawHeight), maxHeightN);
                newPosition.y = Math.max(startPosY - (newSize.height - startHeight), safeTop);
            }

            this.setState({ size: newSize, position: newPosition });
        };

        const onMouseUp = () => {
            this.isResizing = false;
            this.setState({ resizing: false });
            this.detachResizeListeners();
        };

        this.resizeMouseMove = onMouseMove;
        this.resizeMouseUp = onMouseUp;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    startResizeW = e => this.startResize(e, ['w']);
    startResizeE = e => this.startResize(e, ['e']);
    startResizeS = e => this.startResize(e, ['s']);
    startResizeSW = e => this.startResize(e, ['s', 'w']);
    startResizeSE = e => this.startResize(e, ['s', 'e']);
    startResizeN = e => this.startResize(e, ['n']);
    startResizeNW = e => this.startResize(e, ['n', 'w']);
    startResizeNE = e => this.startResize(e, ['n', 'e']);

    render() {
        const { contentItemId, loading, error, runtime, minimized, title } = this.props;
        const { size, position, resizing } = this.state;

        // When minimised: the entire overlay is hidden via CSS but the iframe stays
        // mounted (preserving PCI state per AC3.7). The footer icon restores it.
        const floatStyle = minimized
            ? { display: 'none' }
            : { width: size.width, height: size.height };

        return (
            <Draggable
                handle={`.${styles.pciOverlayHeader}`}
                cancel={`.${styles.pciOverlayHeaderBtn}`}
                position={position}
                onDrag={this.handleDrag}
                bounds={this.getDraggableBounds()}
            >
                <div
                    id={`pciResourceOverlay-${contentItemId}`}
                    data-testid="pciResourceOverlay"
                    className={styles.pciOverlayFloat}
                    style={floatStyle}
                >
                    <header
                        className={styles.pciOverlayHeader}
                        data-testid="pciOverlayHeader"
                        onDoubleClick={this.handleHeaderDoubleClick}
                    >
                        <span className={styles.pciOverlayTitle}>{title}</span>
                        <button
                            className={styles.pciOverlayHeaderBtn}
                            onClick={this.handleMinimize}
                            aria-label={t('general.minimise')}
                            type="button"
                        >
                            <i className="fa fa-compress" aria-hidden="true" />
                        </button>
                        <button
                            className={styles.pciOverlayHeaderBtn}
                            onClick={this.handleClose}
                            aria-label={t('general.close')}
                            type="button"
                        >
                            <i className="fa fa-times" aria-hidden="true" />
                        </button>
                    </header>

                    <div className={styles.pciOverlayBody}>
                        {resizing && <div className={styles.pciResizeCover} />}
                        {loading && (
                            <div className={styles.pciOverlayStatus}>
                                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                                <span>{t('multimedia.actionButton.loading')}</span>
                            </div>
                        )}
                        {!loading && error && (
                            <div className={styles.pciOverlayStatus}>
                                <i className="fa fa-exclamation-circle" aria-hidden="true" />
                                <span>{t('general.failedToLoadResource')}</span>
                            </div>
                        )}
                        {!loading && !error && runtime && (
                            <PciResourceRuntimeHost
                                runtime={runtime}
                                title={title}
                                onLoadError={this.handleRuntimeLoadError}
                            />
                        )}
                    </div>

                    {/* eslint-disable jsx-a11y/no-static-element-interactions */}
                    <div
                        className={styles.pciResizeHandleWest}
                        data-testid="pciResizeHandleWest"
                        aria-hidden="true"
                        onMouseDown={this.startResizeW}
                    />
                    <div
                        className={styles.pciResizeHandleEast}
                        data-testid="pciResizeHandleEast"
                        aria-hidden="true"
                        onMouseDown={this.startResizeE}
                    />
                    <div
                        className={styles.pciResizeHandleSouth}
                        data-testid="pciResizeHandleSouth"
                        aria-hidden="true"
                        onMouseDown={this.startResizeS}
                    />
                    <div
                        className={styles.pciResizeHandleSouthWest}
                        data-testid="pciResizeHandleSouthWest"
                        aria-hidden="true"
                        onMouseDown={this.startResizeSW}
                    />
                    <div
                        className={styles.pciResizeHandleSouthEast}
                        data-testid="pciResizeHandleSouthEast"
                        aria-hidden="true"
                        onMouseDown={this.startResizeSE}
                    />
                    <div
                        className={styles.pciResizeHandleNorth}
                        data-testid="pciResizeHandleNorth"
                        aria-hidden="true"
                        onMouseDown={this.startResizeN}
                    />
                    <div
                        className={styles.pciResizeHandleNorthWest}
                        data-testid="pciResizeHandleNorthWest"
                        aria-hidden="true"
                        onMouseDown={this.startResizeNW}
                    />
                    <div
                        className={styles.pciResizeHandleNorthEast}
                        data-testid="pciResizeHandleNorthEast"
                        aria-hidden="true"
                        onMouseDown={this.startResizeNE}
                    />
                    {/* eslint-enable jsx-a11y/no-static-element-interactions */}
                </div>
            </Draggable>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const overlay = state.ui?.pciResourceOverlays?.[ownProps.contentItemId] || {};
    return {
        loading: !!overlay.loading,
        error: overlay.error || null,
        runtime: overlay.runtime || null,
        minimized: !!overlay.minimized,
        title: overlay.resource?.title || '',
    };
};

export { PciResourceOverlayModal as PciResourceOverlayModalUnconnected };
export default connect(mapStateToProps)(PciResourceOverlayModal);
